import { useEffect, useState } from 'react';
import { db } from '@/lib/firebase';
import { doc, getDoc } from 'firebase/firestore';
import { useAuth } from '@/components/AuthProvider';
import { IndustryCode, IndustryClassificationSettings, TRADITIONAL_INDUSTRY_CODES, PAYFAC_INDUSTRY_CODES } from '../types/industryClassifications';

export interface DynamicIndustryCode {
  code: string;
  description: string;
  identifier?: string;
  enabled: boolean;
  type: 'traditional' | 'payfac';
}

export function useIndustryClassifications(source?: string) {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [traditionalCodes, setTraditionalCodes] = useState<DynamicIndustryCode[]>([]);
  const [payfacCodes, setPayfacCodes] = useState<DynamicIndustryCode[]>([]);
  const [allEnabledCodes, setAllEnabledCodes] = useState<DynamicIndustryCode[]>([]);

  useEffect(() => {
    const loadIndustryClassifications = async () => {
      if (!user || !source) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);

        // Load industry classifications from GLOBAL collection
        const industryDoc = await getDoc(
          doc(db, 'merchantIndustryClassifications', 'config')
        );

        // Helper to merge static codes with Firestore overrides
        const mergeCodes = (staticCodes: IndustryCode[], firestoreCodes: Record<string, IndustryCode> = {}) => {
          // Create a map of Firestore codes for faster lookup
          const firestoreMap = new Map(
            Object.values(firestoreCodes).map(c => [c.code, c])
          );

          return staticCodes.map(staticCode => {
            const firestoreEntry = firestoreMap.get(staticCode.code);
            
            if (firestoreEntry) {
              return {
                code: firestoreEntry.code,
                description: firestoreEntry.description,
                identifier: firestoreEntry.identifier,
                enabled: firestoreEntry.enabled,
                type: staticCode.type,
              };
            }
            
            // If not in firestore, use static code (enabled by default)
            return {
              code: staticCode.code,
              description: staticCode.description,
              identifier: staticCode.identifier,
              enabled: true,
              type: staticCode.type,
            };
          });
        };

        if (industryDoc.exists()) {
          const data = industryDoc.data();
          const sourceData: IndustryClassificationSettings | undefined = data.sources?.[source];

          if (sourceData) {
            // Merge traditional codes
            const traditionalArray: DynamicIndustryCode[] = mergeCodes(
              TRADITIONAL_INDUSTRY_CODES,
              sourceData.traditionalCodes
            );

            // Merge payfac codes
            const payfacArray: DynamicIndustryCode[] = mergeCodes(
              PAYFAC_INDUSTRY_CODES,
              sourceData.payfacCodes
            );

            setTraditionalCodes(traditionalArray);
            setPayfacCodes(payfacArray);

            // Get all enabled codes from both types
            const allEnabled = [
              ...traditionalArray.filter(c => c.enabled),
              ...payfacArray.filter(c => c.enabled),
            ];
            setAllEnabledCodes(allEnabled);
          } else {
            console.warn(`No industry classification data found for source: ${source}, using defaults`);
            // Fallback to defaults if source specific settings not found
            const traditionalArray = TRADITIONAL_INDUSTRY_CODES.map(c => ({ ...c, enabled: true }));
            const payfacArray = PAYFAC_INDUSTRY_CODES.map(c => ({ ...c, enabled: true }));
            
            setTraditionalCodes(traditionalArray);
            setPayfacCodes(payfacArray);
            setAllEnabledCodes([...traditionalArray, ...payfacArray]);
          }
        } else {
          console.warn('No industry classifications document found, using defaults');
          // Fallback to defaults if config doc not found
          const traditionalArray = TRADITIONAL_INDUSTRY_CODES.map(c => ({ ...c, enabled: true }));
          const payfacArray = PAYFAC_INDUSTRY_CODES.map(c => ({ ...c, enabled: true }));
          
          setTraditionalCodes(traditionalArray);
          setPayfacCodes(payfacArray);
          setAllEnabledCodes([...traditionalArray, ...payfacArray]);
        }
      } catch (error) {
        console.error('Error loading industry classifications:', error);
        // Fallback to defaults on error
        const traditionalArray = TRADITIONAL_INDUSTRY_CODES.map(c => ({ ...c, enabled: true }));
        const payfacArray = PAYFAC_INDUSTRY_CODES.map(c => ({ ...c, enabled: true }));
        
        setTraditionalCodes(traditionalArray);
        setPayfacCodes(payfacArray);
        setAllEnabledCodes([...traditionalArray, ...payfacArray]);
      } finally {
        setLoading(false);
      }
    };

    loadIndustryClassifications();
  }, [user?.companyId, source]);

  return {
    loading,
    traditionalCodes,
    payfacCodes,
    allEnabledCodes,
    hasData: allEnabledCodes.length > 0,
  };
}
